import axios from '../utils/axios';
import { sortConfigsByLetter } from '../utils/configSort';

export async function getSessions(queryString: string): Promise<any[]> {
  return axios.get(`/sessions?${queryString}`).then((res) => res.data);
}

export async function updateSession(sessionId: string, data: any): Promise<any> {
  return axios.put(`/sessions/${sessionId}`, data).then((res) => res.data);
}

export async function createSession(data: any): Promise<any> {
  return axios.post('/sessions', data).then((res) => res.data);
}

export async function deleteSession(sessionId: string): Promise<any> {
  return axios.delete(`/sessions/${sessionId}`).then((res) => res.data);
}

// Bespoke requests

export async function getSessionCalcData(sessionId: string): Promise<any> {
  return axios.get(`/sessions/${sessionId}?scope=calculated_data_internal`).then((res) => {
    // Sort configs array if it exists
    if (res.data && Array.isArray(res.data.configs)) {
      res.data.configs = sortConfigsByLetter(res.data.configs);
    }
    return res.data;
  });
}

export async function getSingletonSessions(): Promise<any[]> {
  return axios.get('/sessions?scope=singletons').then((res) => res.data);
}

export async function getSessionCSV(sessionId: string): Promise<any> {
  return axios.get(`/sessions/${sessionId}?scope=csv`, { responseType: 'blob' });
}

export async function getSessionXLS(sessionId: string): Promise<any> {
  return axios.get(`/sessions/${sessionId}?scope=xls`, { responseType: 'blob' });
}

export async function getSessionReport(sessionId: string): Promise<any> {
  return axios.get(`/test-sessions/${sessionId}?scope=xls_report`, { responseType: 'blob' });
}

export async function getTyreReferenceSessions(): Promise<any[]> {
  return axios.get('/sessions?scope=tyre_reference_sessions').then((res) => res.data);
}

export async function getTyreReport(sessionId: string): Promise<any[]> {
  return axios.get(`/sessions/${sessionId}?scope=tyre_report`).then((res) => {
    const sessions = res.data;
    if (!Array.isArray(sessions) || sessions.length <= 1) {
      return sessions;
    }

    // First session is the reference test, keep it first
    const referenceTest = sessions[0];
    const otherTests = sessions.slice(1);

    // Sort other tests by date in descending order (most recent first)
    otherTests.sort((a, b) => {
      const dateA = new Date(a.date || a.created_at).getTime();
      const dateB = new Date(b.date || b.created_at).getTime();
      return dateA - dateB; // ascending order
    });

    return [referenceTest, ...otherTests];
  });
}

export async function updateWeatherData(sessionId: string): Promise<any> {
  return axios.get(`/sessions/${sessionId}?scope=update_weather`).then((res) => res.data);
}

export async function getTyreReportExcel(
  sessionId: string,
  refSpeed?: number,
  psi: boolean = false,
  nearestPair: boolean = false,
): Promise<Blob> {
  let url = `/sessions/${sessionId}?scope=tyre_report_excel&psi=${psi}&nearest_pair=${nearestPair}`;
  if (refSpeed !== undefined) {
    url += `&ref_speed=${refSpeed}`;
  }
  return axios.get(url, { responseType: 'blob' }).then((res) => res.data);
}

// ── Session Command Endpoints (POST) ──────────────────────

export async function duplicateSession(
  sessionId: string,
  mode: 'with_runs' | 'without_runs',
): Promise<any> {
  return axios
    .post(`/sessions/${sessionId}/duplicate`, { mode })
    .then((res) => res.data);
}

export async function setWheelAndReprocess(
  sessionId: string,
): Promise<any> {
  return axios
    .post(`/sessions/${sessionId}/set-wheel-and-reprocess`)
    .then((res) => res.data);
}

export async function assignCalibrationParams(
  sessionId: string,
): Promise<any> {
  return axios
    .post(
      `/sessions/${sessionId}/assign-calibration-params`,
    )
    .then((res) => res.data);
}

export async function assignCrr(
  sessionId: string,
): Promise<any> {
  return axios
    .post(`/sessions/${sessionId}/assign-crr`)
    .then((res) => res.data);
}

export async function reorderConfigs(
  sessionId: string,
  body: Record<string, any>,
): Promise<any> {
  return axios
    .post(`/sessions/${sessionId}/reorder`, body)
    .then((res) => res.data);
}
